import React, { FC, Fragment, useState } from 'react';
import { AppBar, Box, Divider, IconButton, Toolbar, Tooltip, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { toggleSidebar } from 'features/document/documentSlice';
import { Recipient, documentStatus } from 'features/document/constants';
import { formatToTitleCase } from 'utils/formatDocumentType';
import { ViewSidebarOutlined } from '@mui/icons-material';
import ViewSideBar from '@mui/icons-material/ViewSidebar';
import Button from 'components/Button/Button';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import SendFileModal from 'components/Navbar/components/SendFileModal/SendFileModal';
import { usePostTemplateMutation } from 'features/template/templateApi';
import { useNavigate } from 'react-router-dom';
import { selectTemplateDocument } from 'features/document/documentSlice';
import styles from './ViewerNavBar.module.css';
interface ViewerNavBarProps {
  documentInfo: {
    documentName: string;
    documentType: string;
    recipients: Recipient[];
  };
}

const ViewerNavBar: FC<ViewerNavBarProps> = ({ documentInfo }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { documentName, documentType, recipients } = documentInfo;
  const [openSendFileModal, setOpenSendFileModal] = useState(false);
  const isSidebarOpen = useAppSelector(state => state.document.isSidebarOpen);
  const templateJson = useAppSelector(selectTemplateDocument);
  const [postTemplate] = usePostTemplateMutation();

  const handleGoBack = () => {
    navigate('/documents');
  };

  const handleToggleSidebar = () => {
    dispatch(toggleSidebar());
  };

  const handleOpenSendFileModal = () => {
    setOpenSendFileModal(true);
  };

  const handleCloseSendFileModal = () => {
    setOpenSendFileModal(false);
  };

  const handleCreate = async () => {
    const defaultName = `Untitled ${documentType && formatToTitleCase(documentType)} Template`;

    const createdTemplate = await postTemplate({
      name: documentName || defaultName,
      isFavorite: false,
      templateJson: JSON.stringify(templateJson),
      status: !documentName || documentName === defaultName ? documentStatus.Draft : documentStatus.Active,
      type: documentType,
    }).unwrap();
    if (createdTemplate) {
      navigate(`/templates/edit/${documentType.toLowerCase()}/${createdTemplate.data.id}`);
    }
  };

  return (
    <Fragment>
      <AppBar className={styles.viewerAppbar} position='fixed'>
        <Toolbar className={styles.primaryViewerToolbar}>
          <Box display='flex' alignItems='center' gap='1rem' height='40px'>
            <Button variant='tertiary' startIcon={<ArrowBackRoundedIcon />} onClick={handleGoBack}>
              Back
            </Button>
            <Divider className={styles.toolbarDivider} orientation='vertical' variant='middle' />
            {documentName && (
              <Typography className={styles.documentName} variant='h6' component='h1'>
                {documentName}
              </Typography>
            )}
            <Divider className={styles.toolbarDivider} orientation='vertical' variant='middle' />
            {documentType && (
              <Box className={styles.typeTag}>
                <Typography className={styles.tagText}>{formatToTitleCase(documentType)}</Typography>
              </Box>
            )}
          </Box>
          <Box className={styles.renderButtonsWrapper}>
            <p className={styles.viewOnlyText}>View Only</p>
            <Box display='flex' alignItems='center' gap='1rem' height='40px'>
              <Button variant='primary' onClick={handleCreate}>
                Create Template
              </Button>
            </Box>
          </Box>
        </Toolbar>
        <Toolbar className={styles.secondaryViewerToolbar}>
          <Box className={styles.secondaryViewerContainer}>
            <Button variant='secondary' onClick={handleOpenSendFileModal}>
              View Recipients
            </Button>
            <Tooltip arrow title='Show Page Thumbnails' placement='bottom-start'>
              <IconButton onClick={handleToggleSidebar}>
                {isSidebarOpen ? (
                  <ViewSideBar color='primary' className={styles.viewSideBarIcon} />
                ) : (
                  <ViewSidebarOutlined color='primary' className={styles.viewSideBarIcon} />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>
      <SendFileModal
        open={openSendFileModal}
        recipients={recipients}
        isViewMode={true}
        onClose={handleCloseSendFileModal}
      />
    </Fragment>
  );
};

export default ViewerNavBar;
